import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  Fade,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import landNestLogo from './../../../Components/assets/LandNestLogo.jpg';
import axios from 'axios';
import { AuthContext } from './../../AuthContext/AuthContext';
// import { generateToken } from '../../Firebase/Firebase';
import { BASE_URL,Notification_Url } from '../../Api/ApiUrls';

const AdminLoginPage = () => { 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { login } = useContext(AuthContext);

const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setLoading(true);

  try {
    const fcmToken = 123;

    // Step 1: Authenticate with login API
    const loginResponse = await axios.post(`${BASE_URL}/login/`, {
      identifier: email,
      password: password,
      fcm_token: fcmToken || '',
    });

    const data = loginResponse.data;

    if (loginResponse.status === 200 || loginResponse.status === 201) {
      // Step 2: Get all users and find the one matching user_id
      const usersResponse = await axios.get(`${BASE_URL}/users/`);
      const user = usersResponse.data.find(u => u.user_id === data.user_id);

      if (!user) {
        setError('User not found.');
        setLoading(false);
        return;
      }

      // Step 3: Extract role ID from user and fetch roles
      const roleId = parseInt(user.role);
      const rolesResponse = await axios.get(`${BASE_URL}/roles/`);
      const role = rolesResponse.data.find(r => r.role_id === roleId);

      if (!role || role.role_name.toLowerCase() !== 'admin') {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      // Login with session token
      login(data.user_id, data.session_token);

      if (rememberMe) {
        sessionStorage.setItem('isAuthenticated', 'true');
        sessionStorage.setItem('user_id', data.user_id);
        sessionStorage.setItem('session_token', data.session_token);
      }

      // Send notification
      if (fcmToken) {
        try {
          const notifyResponse = await fetch(`${Notification_Url}/send-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token: fcmToken,
              title: 'Welcome to LandNest!',
              body: 'You have successfully logged in.',
            }),
          });

          const notifyData = await notifyResponse.json();

          if (notifyResponse.ok) {
            console.log('Notification sent successfully:', notifyData);
          } else {
            console.error('Failed to send notification:', notifyData);
          }
        } catch (notifyError) {
          console.error('Error sending notification:', notifyError);
        }
      } else {
        console.warn('No FCM token available for notification.');
      }

      navigate('/admin-customers');
    } else if (loginResponse.status === 409) {
      // Handle 409 status - redirect to force login
      navigate("/admin-force-login", {
        state: { identifier: email, fcmToken: fcmToken || '' },
      });
    } else {
      setError(data.message || 'Login failed. Please try again.');
    }
  } catch (err) {
    console.error('Login error:', err);
    if (err.response) {
      if (err.response.status === 401) {
        setError('Invalid email or password');
      } else if (err.response.status === 403) {
        setError('Account not authorized');
      } else if (err.response.status === 409) {
        // This should be handled by the response.status check above, but added here for safety
        navigate("/admin-force-login", {
          state: { identifier: email },
        });
      } else {
        setError(err.response.data.message || 'Login failed. Please try again.');
      }
    } else if (err.request) {
      setError('Network error. Please check your connection.');
    } else {
      setError('An unexpected error occurred.');
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <Fade in timeout={700}>
      <Box
        sx={{
          marginTop: '50px',
          Height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: isMobile ? 2 : 6,
          px: isMobile ? 1 : 4,
        }}
      >
        <Paper
          elevation={isMobile ? 2 : 10}
          sx={{
            width: isMobile ? '100%' : isTablet ? '90%' : '80%',
            maxWidth: isMobile ? '100%' : 1000,
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            height: isMobile ? '100vh' : isTablet ? '80vh' : '70vh',
            boxShadow: isMobile ? 'none' : `
              0 0 15px rgba(0, 0, 0, 0.2),
              0 0 30px rgba(0, 0, 0, 0.1)
            `,
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-5px)',
              boxShadow: isMobile ? 'none' : `
                0 0 25px rgba(0, 0, 0, 0.25),
                0 0 45px rgba(0, 0, 0, 0.15)
              `
            }
          }}
        >
          {/* Left Side - Branding */}
          {!isMobile && (
            <Box
              sx={{
                width: isTablet ? '45%' : '50%',
                background: 'linear-gradient(135deg, #0d0d25, #000)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: isTablet ? 2 : 4,
                position: 'relative',
                '&::after': isTablet ? {} : {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '20px solid transparent',
                  borderBottom: '20px solid transparent',
                  borderLeft: '20px solid #0d0d25'
                }
              }}
            >
              <Box
                component="img"
                src={landNestLogo}
                sx={{
                  width: isTablet ? 200 : 300,
                  height: isTablet ? 170 : 250,
                  mb: isTablet ? 2 : 4,
                  borderRadius: '50%',
                  border: '4px solid #f7e896',
                  boxShadow: '0 0 20px rgba(247, 232, 150, 0.5)'
                }}
              />
              <Typography 
                variant={isTablet ? "h5" : "h4"} 
                sx={{ color: '#f7e896', fontWeight: 600, mb: 1 }}
              >
                LANDNEST
              </Typography>
              <Typography 
                variant={isTablet ? "body1" : "subtitle1"} 
                sx={{ color: 'rgba(255,255,255,0.8)', mb: isTablet ? 2 : 4 }}
              >
                Property Management System
              </Typography>
            </Box>
          )}

          {/* Right Side - Login Form */}
          <Box
            sx={{
              width: isMobile ? '100%' : isTablet ? '55%' : '50%',
              p: isMobile ? 3 : isTablet ? 4 : 6,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: `
                linear-gradient(145deg, rgba(232,224,208,0.95), rgba(216,204,186,0.95)),
                url('https://www.transparenttextures.com/patterns/cream-paper.png')
              `,
              backdropFilter: 'blur(5px)'
            }}
          >
            {isMobile && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Box
                  component="img"
                  src={landNestLogo}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    border: '3px solid #f7e896',
                    boxShadow: '0 0 15px rgba(247, 232, 150, 0.5)'
                  }}
                />
                <Typography 
                  variant="h5" 
                  sx={{ color: '#0d0d25', fontWeight: 600, mt: 2 }}
                >
                  LANDNEST
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: 'rgba(0,0,0,0.7)', mb: 2 }}
                >
                  Property Management System
                </Typography>
                <Divider sx={{ width: '80%', my: 1 }} />
              </Box>
            )}

            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ 
                fontWeight: 600, 
                mb: isMobile ? 2 : 3, 
                textAlign: 'center',
                color: isMobile ? '#0d0d25' : 'inherit'
              }}
            >
              Admin Login
            </Typography>
            
            {error && (
              <Typography 
                color="error" 
                sx={{ 
                  fontSize: 14, 
                  mb: 2, 
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
              >
                {error}
              </Typography>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: isMobile ? 2 : 3 }}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.8)'
                  }
                }}
                required
              />
              
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 1 }}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.8)'
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                required
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={() => setRememberMe(!rememberMe)}
                      sx={{ color: '#2e7d32' }}
                      size={isMobile ? "small" : "medium"}
                    />
                  }
                  label={<Typography variant={isMobile ? "body2" : "body1"}>Remember me</Typography>}
                />
              </Box>

              <Button
                disableRipple 
                disableElevation
                type="submit"
                fullWidth
                variant="contained"
                startIcon={<Lock />}
                disabled={loading}
                sx={{
                  mt: isMobile ? 2 : 3,
                  mb: isMobile ? 1 : 2,
                  py: isMobile ? 1 : 1.5,
                  background: 'linear-gradient(135deg, #f1e57c, #e6ce45)',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ffe85c, #f0d53e)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #e0e0e0, #b0b0b0)',
                    color: '#666'
                  }
                }}
              >
                {loading ? 'Logging in...' : (isMobile ? 'Login' : 'Login to Dashboard')}
              </Button>

              <Typography 
                variant={isMobile ? "caption" : "body2"} 
                sx={{ 
                  color: 'text.secondary', 
                  mt: isMobile ? 2 : 3, 
                  textAlign: 'center',
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}
              >
                Restricted access. Unauthorized entry prohibited.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default AdminLoginPage;